import * as React from 'react';
import { Table, Tag } from 'antd';
import axios from 'axios';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

type Student = {
  wordpressId: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  lastCoupon: string;
  certificates: Object[];
  appointments: Object[];
};

type Company = {
  companyName: string;
  companyCode: string;
};

export default function StudentsTable() {
  const [students, setStudents] = React.useState<Student[]>([]);
  const [studentFilters, setStudentFilters] = React.useState([]);
  const [companies, setCompanies] = React.useState([]);
  const [companyFilters, setCompanyFilters] = React.useState([]);
  const [studentsUpdated, setStudentsUpdated] = React.useState(false);
  const [isModalVisible, setModalVisible] = React.useState(false);

  // Get students and companies from the database and set table filters for them
  React.useEffect(() => {
    // Get all the students
    axios
      .get('/api/student')
      .then((res) => {
        setStudents(res.data);

        // Set the student filters
        const studentFilters = res.data.map((student: Student) => {
          return {
            text: `${student.firstName} ${student.lastName}`,
            value: `${student.firstName} ${student.lastName}`,
          };
        });

        setStudentFilters(studentFilters);
      })
      .then(() => {
        // Get all the companies
        axios.get('/api/company').then((res) => {
          setCompanies(res.data);

          // Set the company filters
          const companyFilters = res.data.map((company: Company) => {
            return {
              text: company.companyName,
              value: company.companyName,
            };
          });

          setCompanyFilters(companyFilters);
        });
      });
  }, []);

  // Within the students array, if there is a coupon, add the associated company as well
  React.useEffect(
    () => {
      if (students.length > 0 && companies.length > 0) {
        const newStudents: Student[] = students.map((student: Student) => {
          if (student.lastCoupon) {
            let foundCompany: any = companies.find(
              (company: Company) => company.companyCode.toUpperCase() === student.lastCoupon.toUpperCase()
            );

            if (foundCompany?.companyName) {
              student.company = foundCompany.companyName;
            }
          }
          return student;
        });

        setStudents(newStudents);
        setStudentsUpdated(true);
      }
    },
    // Can use companies as a dependency here safely because it is only set AFTER the students are updated
    [companies]
  );

  const columns: Object[] = [
    {
      title: 'Full Name',
      dataIndex: 'firstName',
      filters: studentFilters,
      filterSearch: true,
      onFilter: (value: any, record: any) => record.address.startsWith(value),
      render: (text: string, record: any) => {
        return (
          <Link to={`/student/${record._id}`}>
            {record.firstName} {record.lastName}
          </Link>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
    },
    {
      title: 'Phone Number',
      dataIndex: 'phone',
    },
    {
      title: 'Company',
      dataIndex: 'company',
      filters: companyFilters,
      filterSearch: true,
      onFilter: (value: string, record: any) => record.address.startsWith(value),
    },
    {
      title: 'Certificates',
      dataIndex: 'certificates',
      render: (text: any, record: any) => {
        if (record.certificates.length > 0) {
          return (
            <span>
              {record.certificates.map((certificate: any) => {
                if (dayjs().isBefore(certificate.expiryDate)) {
                  return <Tag color="green">{certificate.certificateName}</Tag>;
                }
              })}
            </span>
          );
        } else {
          return '-';
        }
      },
    },
  ];

  return (
    <>
      {studentsUpdated ? (
        <Table columns={columns} dataSource={[...students]} rowKey="_id" />
      ) : (
        // TODO: Change to loading icon
        <>Loading...</>
      )}
    </>
  );
}
